import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { ARROW_SIDE_SCROLL_CONTAINER_ITEM_CLASSNAME } from '@lumapps/lumx-layouts/components/ArrowSideScrollContainer';
import { mdiHome, mdiChevronRight } from '@lumapps/lumx/icons';
import { ColorPalette, Icon, Link, type LinkProps, Size, Text, Typography } from '@lumapps/lumx/react';
import { DangerousReactRouterLink } from '@lumapps/router';
import { addCustomVersionParamsToUrl } from '@lumapps/router/utils';

import { BreadCrumbLevel } from '../../types';

import './index.scss';

export interface BreadcrumbItemProps {
    /** Classname to set li */
    className?: string;
    /** The data for the breadcrumb level to be rendered. */
    item: BreadCrumbLevel;
    /** Whether this item is the last one in the breadcrumb trail, used for styling and accessibility. */
    isLast: boolean;
    /**
     * Whether the breadcrumb is displayed with minimal spacing, affecting the separator icon's style.
     * @default false
     * */
    isInline?: boolean;
}

const CLASSNAME = 'lumx-breadcrumb-item';

/**
 * Renders a single item within a Breadcrumb.
 * It displays as a clickable link for intermediate levels and as plain text for the last level.
 * Navigation is handled by prioritizing an `onClick` handler, falling back to a `url` for routing.
 * A home icon is used as a fallback if a link item has no label.
 */
export const BreadcrumbItem = ({ className, item, isLast, isInline = false }: BreadcrumbItemProps) => {
    const { block, element } = useClassnames(CLASSNAME);

    const linkProps: LinkProps = {
        className: element('link'),
        typography: Typography.subtitle1,
        color: ColorPalette.dark,
        'aria-label': item.ariaLabel,
        ...item.dataAttributes,
        ...(item.onClick
            ? { onClick: item.onClick }
            : {
                  to: item.url ? addCustomVersionParamsToUrl(item.url) : item.url,
                  linkAs: DangerousReactRouterLink,
              }),
    };

    const textProps = isLast
        ? {
              color: ColorPalette.secondary,
              'aria-current': 'page',
          }
        : undefined;

    const textLabel = item.label ? (
        <Text as="span" truncate={!isInline} whiteSpace="nowrap" typography={Typography.subtitle1} {...textProps}>
            {item.label}
        </Text>
    ) : null;

    return (
        <li className={block({ inline: isInline }, [className, ARROW_SIDE_SCROLL_CONTAINER_ITEM_CLASSNAME])}>
            {isLast ? (
                textLabel
            ) : (
                <>
                    <Link {...linkProps}>
                        {textLabel || <Icon icon={mdiHome} className={element('home-icon')} size={Size.xs} />}
                    </Link>

                    <Icon
                        icon={mdiChevronRight}
                        className={element('chevron-icon')}
                        size={Size.xs}
                        aria-hidden="true"
                    />
                </>
            )}
        </li>
    );
};
